import { TOKEN_FIELDS } from './token-fields'
import { swapChipToken } from './token-chips'

// ============================================================
// Click-menu for a chip inside the editing overlay: swap which
// field it fills in with, or remove it. Plain DOM (no tempo):
// the menu is a transient singleton on document.body, and all
// interactions go through mousedown+preventDefault so focus
// never leaves the contenteditable overlay (a blur would commit
// and tear the overlay down mid-interaction).
// ============================================================

let activeMenu: HTMLElement | null = null

function closeChipMenu() {
  activeMenu?.remove()
  activeMenu = null
  document.removeEventListener('mousedown', onOutsideMousedown, true)
  document.removeEventListener('keydown', onEscape, true)
}

function onOutsideMousedown(e: MouseEvent) {
  if (activeMenu && !activeMenu.contains(e.target as Node)) closeChipMenu()
}

function onEscape(e: KeyboardEvent) {
  if (e.key === 'Escape' && activeMenu) {
    e.stopPropagation() // don't also cancel the text edit
    closeChipMenu()
  }
}

/** Tell the overlay its content changed (height resync, etc.). */
function notifyOverlay(overlay: HTMLElement) {
  overlay.dispatchEvent(new Event('input', { bubbles: true }))
}

export function openChipMenu(chip: HTMLElement, overlay: HTMLElement): void {
  closeChipMenu()

  const menu = document.createElement('div')
  menu.className = 'rh-chip-menu'
  const current = chip.getAttribute('data-token')

  const item = (label: string, onPick: () => void, cls = '') => {
    const el = document.createElement('div')
    el.className = `rh-chip-menu-item ${cls}`.trim()
    el.textContent = label
    // preventDefault keeps focus (and the selection) in the overlay.
    el.addEventListener('mousedown', e => e.preventDefault())
    el.addEventListener('click', () => {
      onPick()
      closeChipMenu()
      notifyOverlay(overlay)
    })
    menu.appendChild(el)
    return el
  }

  const head = document.createElement('div')
  head.className = 'rh-chip-menu-head'
  head.textContent = 'Fills in with…'
  menu.appendChild(head)

  for (const field of TOKEN_FIELDS) {
    const el = item(field.label, () => swapChipToken(chip, field.token))
    if (field.token === current) el.classList.add('current')
  }

  const sep = document.createElement('div')
  sep.className = 'rh-chip-menu-sep'
  menu.appendChild(sep)

  item('Remove — delete this field', () => chip.remove(), 'danger')

  // position: fixed in viewport coordinates, so the canvas zoom/transform
  // can't skew placement; clamped to the viewport.
  const rect = chip.getBoundingClientRect()
  menu.style.left = '0px'
  menu.style.top = '0px'
  document.body.appendChild(menu)
  const size = menu.getBoundingClientRect()
  const left = Math.min(rect.left, window.innerWidth - size.width - 8)
  const top =
    rect.bottom + size.height + 8 > window.innerHeight
      ? rect.top - size.height - 4
      : rect.bottom + 4
  menu.style.left = `${Math.max(8, left)}px`
  menu.style.top = `${Math.max(8, top)}px`

  activeMenu = menu
  document.addEventListener('mousedown', onOutsideMousedown, true)
  document.addEventListener('keydown', onEscape, true)
}

/** The overlay is being torn down — never leave an orphaned menu. */
export function disposeChipMenu(): void {
  closeChipMenu()
}
